import { useMemo, useState } from 'react';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { formatBangkokRange, formatBangkokDateTime, toBangkokDate } from './utils/bangkokTime';
import { mergeScenarioBatches } from './utils/scenarioState';

// Read-only Gantt-style timeline, grouped by physical pot. IMPLEMENTATION_PLAN.md Phase 2:
// "Gantt ใช้ planned_start/end ต่อ physical pot, setup activities เป็นแถบแยก, แสดง ready/due ใน
// tooltip/detail/table" and "Timeline ระยะแรก read-only; ยังไม่ทำ drag-and-drop reschedule จน
// backend validator ครบ" — so this renders bars but never lets you drag them.
//
// setupActivities is passed in separately (its own endpoint / array) and rendered as its own
// bar type rather than folded into batch bars, so a cleaning/changeover interval is never
// mistaken for production time.
export default function ScheduleTimeline({ scenario, setupActivities = [] }) {
  const batches = useMemo(() => mergeScenarioBatches(scenario), [scenario]);

  const pots = useMemo(() => {
    const potIds = new Set();
    batches.forEach((batch) => potIds.add(String(batch.pot_id ?? 'unassigned')));
    setupActivities.forEach((activity) => potIds.add(String(activity.pot_id ?? 'unassigned')));
    return Array.from(potIds).sort();
  }, [batches, setupActivities]);

  const { minTime, maxTime } = useMemo(() => {
    const times = [];
    batches.forEach((b) => {
      if (b.planned_start) times.push(toBangkokDate(b.planned_start).getTime());
      if (b.planned_end) times.push(toBangkokDate(b.planned_end).getTime());
    });
    setupActivities.forEach((a) => {
      if (a.start_time) times.push(toBangkokDate(a.start_time).getTime());
      if (a.end_time) times.push(toBangkokDate(a.end_time).getTime());
    });
    if (!times.length) return { minTime: null, maxTime: null };
    return { minTime: Math.min(...times), maxTime: Math.max(...times) };
  }, [batches, setupActivities]);

  const [selectedBatchId, setSelectedBatchId] = useState(null);

  if (!batches.length) {
    return <Typography color="text.secondary">No batches on this scenario yet.</Typography>;
  }

  const span = minTime != null && maxTime != null ? Math.max(maxTime - minTime, 60 * 1000) : 1;

  const barLeft = (start) => `${((toBangkokDate(start).getTime() - minTime) / span) * 100}%`;
  const barWidth = (start, end) => `${Math.max(((toBangkokDate(end).getTime() - toBangkokDate(start).getTime()) / span) * 100, 0.5)}%`;

  const selectedBatch = batches.find((b) => b.id === selectedBatchId);

  return (
    <Stack spacing={2}>
      <Typography variant="caption" color="text.secondary">
        Times shown in Asia/Bangkok. Bars are read-only; use the actions above to change the plan.
      </Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ minWidth: 900 }}>
          {pots.map((potId) => (
            <Box key={potId} sx={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider', py: 1 }}>
              <Box sx={{ width: 96, flexShrink: 0, fontWeight: 600 }}>Pot {potId}</Box>
              <Box sx={{ position: 'relative', flexGrow: 1, height: 36, bgcolor: 'action.hover', borderRadius: 1 }}>
                {batches.filter((b) => String(b.pot_id ?? 'unassigned') === potId).map((batch) => (
                  <Tooltip
                    key={batch.id}
                    title={
                      <Box sx={{ fontSize: 12 }}>
                        <div>{batch.sku} · {batch.line}</div>
                        <div>{formatBangkokRange(batch.planned_start, batch.planned_end)}</div>
                        <div>Status: {batch.status}</div>
                        {batch.planned_ready_at && <div>Ready: {formatBangkokDateTime(batch.planned_ready_at)}</div>}
                        {batch.due_at && <div>Due: {formatBangkokDateTime(batch.due_at)}</div>}
                        {batch.isPreserved && <div>Preserved from scenario #{batch.sourceScenarioId}</div>}
                      </Box>
                    }
                  >
                    <Box
                      onClick={() => setSelectedBatchId(batch.id)}
                      sx={{
                        position: 'absolute',
                        left: barLeft(batch.planned_start),
                        width: barWidth(batch.planned_start, batch.planned_end),
                        top: 2, bottom: 2,
                        bgcolor: batch.isPreserved ? 'warning.main' : (batch.id === selectedBatchId ? 'primary.dark' : 'primary.main'),
                        borderRadius: 0.5,
                        color: 'primary.contrastText',
                        fontSize: 11,
                        px: 0.5,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                      }}
                    >
                      {batch.sku}
                    </Box>
                  </Tooltip>
                ))}
                {setupActivities.filter((a) => String(a.pot_id ?? 'unassigned') === potId).map((activity, index) => (
                  <Tooltip key={activity.id ?? index} title={`Setup / cleaning: ${formatBangkokRange(activity.start_time, activity.end_time)}`}>
                    <Box
                      sx={{
                        position: 'absolute',
                        left: barLeft(activity.start_time),
                        width: barWidth(activity.start_time, activity.end_time),
                        top: 2, bottom: 2,
                        bgcolor: 'grey.500',
                        backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.3) 0, rgba(255,255,255,0.3) 4px, transparent 4px, transparent 8px)',
                        borderRadius: 0.5,
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Box component="table" sx={{ width: '100%', minWidth: 900, borderCollapse: 'collapse', '& th, & td': { p: 0.75, borderBottom: '1px solid', borderColor: 'divider' }, '& th': { textAlign: 'left', fontWeight: 600 } }}>
          <thead>
            <tr>{['Pot', 'SKU', 'Line', 'Planned (Bangkok)', 'Ready', 'Due', 'Status', 'Locked', 'Source'].map((label) => <th key={label}>{label}</th>)}</tr>
          </thead>
          <tbody>
            {batches.map((batch) => (
              <tr
                key={batch.id}
                onClick={() => setSelectedBatchId(batch.id)}
                style={{ cursor: 'pointer', background: batch.id === selectedBatchId ? 'rgba(25,118,210,0.08)' : undefined }}
              >
                <td>{batch.pot_id}</td>
                <td>{batch.sku}</td>
                <td>{batch.line}</td>
                <td>{formatBangkokRange(batch.planned_start, batch.planned_end)}</td>
                <td>{batch.planned_ready_at ? formatBangkokDateTime(batch.planned_ready_at) : '—'}</td>
                <td>{batch.due_at ? formatBangkokDateTime(batch.due_at) : '—'}</td>
                <td>{batch.status}</td>
                <td>{batch.is_locked ? 'Yes' : 'No'}</td>
                <td>{batch.isPreserved ? <Chip size="small" color="warning" label={`Preserved · #${batch.sourceScenarioId ?? '—'}`} /> : 'This scenario'}</td>
              </tr>
            ))}
          </tbody>
        </Box>
      </Box>

      {selectedBatch && (
        <Typography variant="caption" color="text.secondary">
          Selected batch #{selectedBatch.id} — open Batches to run lifecycle actions on it.
        </Typography>
      )}
    </Stack>
  );
}
